<template>
  <div class="action-buttons" :class="{ 'toggle-mode': isToggleMode }">
    <ActionButton
      v-for="(action, index) in actions"
      :key="resolveActionKey(action, index)"
      :label="action.label"
      :image-src="action.imageSrc"
      :class="{
        'is-toggle': isToggleMode,
        'is-active': isActionActive(action, index),
      }"
      @click="handleActionClick(action, index, $event)"
    >
      <template #icon>
        <component :is="action.icon" v-if="action.icon && !action.imageSrc" />
      </template>
    </ActionButton>
  </div>
</template>

<script>
import ActionButton from "@/components/ActionButton.vue";

function isValidAction(action) {
  if (!action || typeof action !== "object" || Array.isArray(action)) {
    return false;
  }

  const hasValidLabel =
    typeof action.label === "string" && action.label.trim().length > 0;
  const hasValidKey =
    action.key === undefined ||
    typeof action.key === "string" ||
    typeof action.key === "number";
  const hasValidIcon =
    action.icon === undefined ||
    typeof action.icon === "function" ||
    typeof action.icon === "object" ||
    typeof action.icon === "string";
  const hasValidImageSrc =
    action.imageSrc === undefined || typeof action.imageSrc === "string";
  const hasValidClickHandler =
    action.clickHandler === undefined ||
    typeof action.clickHandler === "function";

  return hasValidLabel && hasValidKey && hasValidIcon && hasValidImageSrc && hasValidClickHandler;
}

export default {
  name: "ActionButtonsSection",
  components: { ActionButton },
  props: {
    actions: {
      type: Array,
      default: function () {
        return [];
      },
      validator: function (list) {
        return Array.isArray(list) && list.every(isValidAction);
      },
    },
    mode: {
      type: String,
      default: "action",
      validator: function (value) {
        return ["action", "toggle"].indexOf(value) !== -1;
      },
    },
    value: {
      type: [String, Number],
      default: undefined,
    },
    defaultActive: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      internalActiveKey: this.defaultActive,
    };
  },
  computed: {
    isToggleMode() {
      return this.mode === "toggle";
    },
    activeKey() {
      if (!this.isToggleMode) {
        return null;
      }
      return this.value !== undefined ? this.value : this.internalActiveKey;
    },
  },
  methods: {
    resolveActionKey(action, index) {
      return action && action.key !== undefined ? action.key : index;
    },
    isActionActive(action, index) {
      return this.resolveActionKey(action, index) === this.activeKey;
    },
    handleActionClick(action, index, event) {
      const key = this.resolveActionKey(action, index);

      if (this.isToggleMode) {
        if (this.value === undefined) {
          this.internalActiveKey = key;
        }
        this.$emit("input", key);
      }

      if (action && typeof action.clickHandler === "function") {
        action.clickHandler(event, action, key);
      }
    },
  },
};
</script>

<style scoped>
.action-buttons {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  padding: 0px 16px;
  gap: 16px;
  width: 100%;
  min-height: 56px;
  height: auto;
  margin-bottom: 0px;
}

.action-buttons.toggle-mode {
  gap: 8px;
}

.action-buttons.toggle-mode >>> .btn-action {
  background: #B0ECCA;
  color: #91959A;
  border-radius: 10px;
}

.action-buttons.toggle-mode >>> .btn-action.is-active {
  background: #00b14d;
  color: #ffffff;
}

.action-buttons.toggle-mode >>> .btn-action:active {
  background: #dfe5ea;
}

.action-buttons.toggle-mode >>> .btn-action.is-active:active {
  background: #009940;
}
</style>
